/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Row,
  Col,
  Typography,
  Button,
  Tooltip,
} from 'antd';
import {
  VirtualizedTable,
} from '@deltaohm/ant-components';
import { DownloadOutlined } from '@ant-design/icons';

import {
  getLocale,
  formatLocaleWithTimezone,
} from '../../../utils/date';

import { pascalToDecibel } from '../../../utils/converter';
import { DB_DECIMAL, exportExcel } from '../../charts/utils';

const TIMESTAMP_WIDTH = 250;
const VALUE_WIDTH = 125;

const SingleMeasureTableModal = (props) => {
  const {
    soundSession,
    measure,
  } = props;

  const { timezone } = soundSession;

  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);

  const dataSource = React.useMemo(() => measure.values, [measure]);

  const downloadExcel = () => {
    const { config } = measure;
    const parameters = config.map((conf) => conf.label);
    const parametersMeasureIds = config.map((conf) => conf.measureId);
    const header = ['from', 'to', 'fromDate', 'toDate', ...parameters];

    const data = dataSource.map((ds) => ([
      ds.fromTimestamp,
      ds.toTimestamp,
      formatLocaleWithTimezone(ds.fromTimestamp, 'yyyy-MM-dd HH:mm:ss.SSS', locale, timezone),
      formatLocaleWithTimezone(ds.toTimestamp, 'yyyy-MM-dd HH:mm:ss.SSS', locale, timezone),
      // eslint-disable-next-line max-len
      ...parametersMeasureIds.map((id) => {
        const d = ds.data[id];
        if (d == null) {
          return '';
        }
        return pascalToDecibel(ds.data[id], DB_DECIMAL).toFixed(DB_DECIMAL);
      }),
    ]));

    exportExcel(
      `${soundSession.name}_EVENT_${soundSession.timezone}.xlsx`,
      'Event Table',
      [
        header,
        ...data,
      ],
    );
  };

  const leftColumns = React.useMemo(() => {
    const result = [
      {
        title: 'fromTimestamp',
        dataIndex: 'fromTimestamp',
        render: (fromTimestamp) => {
          const text = formatLocaleWithTimezone(fromTimestamp, 'yyyy/MM/dd HH:mm:ss:SSS', locale, timezone, false);
          return (
            <Typography.Text style={{ fontFamily: 'monospace' }}>
              {text}
            </Typography.Text>
          );
        },
        width: TIMESTAMP_WIDTH,
      },
      {
        title: 'toTimestamp',
        dataIndex: 'toTimestamp',
        render: (toTimestamp) => {
          const text = formatLocaleWithTimezone(toTimestamp, 'yyyy/MM/dd HH:mm:ss:SSS', locale, timezone, false);
          return (
            <Typography.Text style={{ fontFamily: 'monospace' }}>
              {text}
            </Typography.Text>
          );
        },
        width: TIMESTAMP_WIDTH,
      },
    ];
    return result;
  }, [timezone, locale]);

  const columns = React.useMemo(() => {
    const { config } = measure;

    const result = [
      ...config.map((conf) => ({
        title: conf.label,
        dataIndex: '',
        render: (_, record) => {
          let text;
          const currentValue = record.data[conf.measureId];
          if (currentValue != null) {
            text = pascalToDecibel(currentValue, DB_DECIMAL).toFixed(DB_DECIMAL);
          }
          return (
            <div style={{ textAlign: 'right', width: '100%' }}>
              <Typography.Text style={{ fontFamily: 'monospace' }}>
                {text}
              </Typography.Text>
            </div>
          );
        },
        width: VALUE_WIDTH,
      })),
    ];
    return result;
  }, [measure]);

  return (
    <>
      <Row justify="end">
        <Col>
          <Tooltip title={t('soundMeasures.components.charts.excel')}>
            <Button shape="circle" size="small" type="primary" onClick={downloadExcel}>
              <DownloadOutlined />
            </Button>
          </Tooltip>
        </Col>
      </Row>
      <div style={{ height: 10 }} />
      <VirtualizedTable
        height={500}
        bordered
        size="small"
        leftColumns={leftColumns}
        columns={columns}
        rowKey={(record) => record.timestamp}
        dataSource={dataSource}
        pagination={false}
      />
    </>
  );
};

const propTypes = {
  soundSession: PropTypes.shape({
    name: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
  }).isRequired,
  measure: PropTypes.shape({
    kind: PropTypes.string,
    config: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    values: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

SingleMeasureTableModal.propTypes = propTypes;

export default SingleMeasureTableModal;
